import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Staff sign-in, reached from Settings on the public request page — submitting
 * a request needs no sign-in, so this is for logistics and admin. Two paths:
 *  - Entra SSO (redirect) for staff with a DCVFD account
 *  - Magic link for everyone else — we collect a name + email, mail a one-time
 *    link, and the worker sets the session cookie when it's opened.
 *
 * Only @dcvfd.org / @pwcgov.org addresses are accepted on either path.
 */

/** Messages for the ?error= codes the verify endpoint redirects back with. */
const VERIFY_ERRORS: Record<string, string> = {
  'missing-token': 'That sign-in link was incomplete. Request a new one below.',
  'expired-link': 'That sign-in link has expired or was already used. Request a new one below.',
  'invalid-link': 'That sign-in link was not valid. Request a new one below.',
  'not-authorized': 'That email address is not authorized for this application.',
  deactivated: 'That account has been deactivated. Contact an administrator.',
};

export default function Login() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(() => {
    const code = new URLSearchParams(window.location.search).get('error');
    return code ? (VERIFY_ERRORS[code] ?? 'Sign-in failed. Request a new link below.') : null;
  });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function requestLink(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      setError('Enter your name and email.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Direct fetch (not apiFetch) so the worker's { error } message can be
      // shown verbatim — rejections here are user-actionable (wrong domain,
      // rate limited) rather than generic failures.
      const res = await fetch('/api/auth/magic-link/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ name: name.trim(), email: email.trim() }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        setError(data?.error ?? 'Could not send the sign-in link. Try again.');
        return;
      }
      setSent(true);
    } catch {
      setError('Could not send the sign-in link. Check your connection and try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-dvh flex items-center justify-center bg-surface px-4">
      <div className="glass w-full max-w-sm rounded-2xl p-6 text-white">
        <div className="text-center mb-6">
          <img src="/dcvfd-badge.svg" alt="DCVFD" className="h-14 w-auto mx-auto mb-3" />
          <h1 className="text-xl font-bold">Staff sign-in</h1>
          <p className="text-zinc-400 text-sm">Supply Orders · logistics and admin</p>
        </div>

        <a
          href="/api/auth/entra/login"
          className="block w-full text-center rounded-lg bg-dcvfd-accent py-2.5 font-semibold hover:brightness-110"
        >
          Sign in with DCVFD account
        </a>

        <div className="my-5 flex items-center gap-3 text-xs text-zinc-500">
          <span className="h-px flex-1 bg-border-subtle" />
          or get a sign-in link
          <span className="h-px flex-1 bg-border-subtle" />
        </div>

        {sent ? (
          <div className="text-center">
            <p className="text-sm text-zinc-300">
              Check <span className="font-semibold text-white">{email}</span> for a sign-in link. It expires in 30
              minutes and works once.
            </p>
            <button
              type="button"
              onClick={() => setSent(false)}
              className="mt-4 text-xs text-zinc-400 underline hover:text-white"
            >
              Use a different email
            </button>
          </div>
        ) : (
          <form onSubmit={requestLink} className="space-y-3">
            <input
              type="text"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="w-full rounded-lg bg-surface-raised border border-border-subtle px-3 py-2.5 text-sm"
            />
            <input
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@dcvfd.org"
              className="w-full rounded-lg bg-surface-raised border border-border-subtle px-3 py-2.5 text-sm"
            />
            {error && <p className="text-sm text-ems-red">{error}</p>}
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-lg bg-dcvfd-light py-2.5 font-semibold hover:brightness-110 disabled:opacity-50"
            >
              {busy ? 'Sending…' : 'Email me a link'}
            </button>
            <p className="text-center text-xs text-zinc-500">
              Use your @dcvfd.org or @pwcgov.org address. No password needed.
            </p>
          </form>
        )}

        <p className="mt-6 border-t border-border-subtle pt-4 text-center text-xs text-zinc-400">
          Just placing a request? No sign-in needed —{' '}
          <Link to="/request" className="text-white underline">
            go to the request form
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
